import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Article, ArticleListResponse, Category, Tag } from '../types'
import type { OrderBy, OrderDir } from '../types'

export const useNewsStore = defineStore('news', () => {
  const articles = ref<Article[]>([])
  const featuredArticles = ref<Article[]>([])
  const currentArticle = ref<Article | null>(null)
  const relatedArticles = ref<Article[]>([])
  const categories = ref<Category[]>([])
  const tags = ref<Tag[]>([])
  const total = ref(0)
  const loading = ref(false)

  async function fetchArticles(params: {
    page?: number
    page_size?: number
    category_id?: number
    tag_id?: number
    order_by?: OrderBy
    order?: OrderDir
  } = {}) {
    loading.value = true
    try {
      const searchParams = new URLSearchParams()
      searchParams.set('page', String(params.page || 1))
      searchParams.set('page_size', String(params.page_size || 12))
      if (params.category_id) searchParams.set('category_id', String(params.category_id))
      if (params.tag_id) searchParams.set('tag_id', String(params.tag_id))
      if (params.order_by) searchParams.set('order_by', params.order_by)
      if (params.order) searchParams.set('order', params.order)

      const response = await fetch(`/api/articles?${searchParams}`)
      const result = await response.json()
      if (result.code === 0) {
        const data = result.data as ArticleListResponse
        articles.value = data.items
        total.value = data.total
      }
    } finally {
      loading.value = false
    }
  }

  async function fetchFeaturedArticles() {
    try {
      const response = await fetch('/api/articles?page=1&page_size=5&order_by=view_count&order=desc')
      const result = await response.json()
      if (result.code === 0) {
        featuredArticles.value = result.data.items
      }
    } catch { /* empty */ }
  }

  async function fetchArticleDetail(id: number) {
    loading.value = true
    try {
      const response = await fetch(`/api/articles/${id}`)
      const result = await response.json()
      if (result.code === 0) {
        currentArticle.value = result.data
      }
    } finally {
      loading.value = false
    }
  }

  async function fetchRelatedArticles(articleId: number) {
    try {
      const response = await fetch(`/api/articles/${articleId}/related?page=1&page_size=4`)
      const result = await response.json()
      if (result.code === 0) {
        relatedArticles.value = result.data.items || []
      }
    } catch {
      relatedArticles.value = []
    }
  }

  async function fetchCategories() {
    try {
      const response = await fetch('/api/categories')
      const result = await response.json()
      if (result.code === 0) {
        categories.value = result.data
      }
    } catch { /* empty */ }
  }

  async function fetchTags() {
    try {
      const response = await fetch('/api/tags')
      const result = await response.json()
      if (result.code === 0) {
        tags.value = result.data
      }
    } catch { /* empty */ }
  }

  return {
    articles,
    featuredArticles,
    currentArticle,
    relatedArticles,
    categories,
    tags,
    total,
    loading,
    fetchArticles,
    fetchFeaturedArticles,
    fetchArticleDetail,
    fetchRelatedArticles,
    fetchCategories,
    fetchTags,
  }
})
